import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { formatDate } from '../../utils/Utils';
import { useMyPip } from './config';
import { PIP_STATUS_COLORS, StatusBadge } from '../pip/config';

// P1 Görev Paketi A — "PIP Sürecim" (çalışan, SALT-OKUNUR): hedefler, süre,
// check-in'ler. MyCalibrationPage'in sayfa desenini izler (Card, DataTable yok).
// Taslak/iptal edilmiş süreçler BE tarafından hiç dönmez — çalışan yalnız
// yürürlükteki (active) ve tamamlanmış süreçleri görür.
const Container = styled.div``;

const PageHeader = styled.div`
  margin-bottom: 18px;
`;

const PageTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.xl};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  padding: 20px 22px;
`;

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 24px;
  text-align: center;
`;

const EmptyText = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  max-width: 420px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
`;

const Section = styled.div`
  margin-bottom: 22px;
  padding-top: 18px;
  border-top: 1px solid #E8ECF0;
`;

const SectionTitle = styled.h4`
  margin: 0 0 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const MetaRow = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 6px;
`;

const MetaLabel = styled.span`
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  margin-right: 6px;
`;

const GoalCard = styled.div`
  background: ${({ theme }) => theme.colors.bgColor};
  border: 1px solid ${({ theme }) => theme.colors.surface?.border || '#E8ECF0'};
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 10px;
  font-size: 13px;
`;

const CheckInCard = styled.div`
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 8px;
  font-size: 13px;
`;

const SmallMeta = styled.div`
  color: ${({ theme }) => theme.colors.text.dark.little};
  font-size: 12px;
  margin-top: 4px;
`;

const AchievedBadge = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  color: ${(p) => (p.$achieved ? '#16A34A' : '#DC2626')};
  background: ${(p) => (p.$achieved ? '#DCFCE7' : '#FEF2F2')};
`;

const PastList = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const PastChip = styled.button`
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary.main : '#E8ECF0')};
  background: ${({ $active }) => ($active ? '#EFF6FF' : '#fff')};
  color: ${({ theme }) => theme.colors.text.primary};
  border-radius: 16px;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
`;

export default function MyPipPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const {
    list, current, detail, fetching, detailFetching, selectPip,
  } = useMyPip();

  const statusCfg = current ? (PIP_STATUS_COLORS[current.status] || PIP_STATUS_COLORS.active) : null;

  return (
    <Container>
      <PageHeader>
        <PageTitle>{t('performance.pip.myTitle')}</PageTitle>
      </PageHeader>

      {fetching && (
        <Card><Centered><EmptyText>{t('common.loading')}</EmptyText></Centered></Card>
      )}

      {!fetching && !current && (
        <Card><Centered><EmptyText>{t('performance.pip.myEmpty')}</EmptyText></Centered></Card>
      )}

      {!fetching && current && (
        <Card>
          {list.length > 1 && (
            <PastList>
              {list.map((r) => (
                <PastChip
                  key={r.id}
                  type="button"
                  $active={r.id === current.id}
                  onClick={() => selectPip(r.id)}
                >
                  {r.startedAt ? formatDate(r.startedAt, lang) : formatDate(r.createDate, lang)}
                  {' · '}
                  {t(`performance.pip.status.${r.status}`, { defaultValue: r.status })}
                </PastChip>
              ))}
            </PastList>
          )}

          <InfoRow>
            {statusCfg && (
              <StatusBadge color={statusCfg.color} bg={statusCfg.bg}>
                {t(`performance.pip.status.${current.status}`, { defaultValue: current.status })}
              </StatusBadge>
            )}
          </InfoRow>

          <MetaRow>
            <MetaLabel>{t('performance.pip.durationLabel')}:</MetaLabel>
            {current.durationDays} {t('performance.pip.daysSuffix')}
          </MetaRow>
          {current.startedAt && (
            <MetaRow>
              <MetaLabel>{t('performance.pip.startedAt')}:</MetaLabel>
              {formatDate(current.startedAt, lang)}
            </MetaRow>
          )}
          {current.dueDate && (
            <MetaRow>
              <MetaLabel>{t('performance.pip.dueDate')}:</MetaLabel>
              {formatDate(current.dueDate, lang)}
            </MetaRow>
          )}

          {detailFetching && <EmptyText>{t('common.loading')}...</EmptyText>}

          {!detailFetching && detail && (
            <>
              {detail.closureNote && (
                <MetaRow>
                  <MetaLabel>{t('performance.pip.closureNote')}:</MetaLabel>
                  {detail.closureNote}
                </MetaRow>
              )}

              <Section>
                <SectionTitle>{t('performance.pip.goals')}</SectionTitle>
                {(detail.goals || []).length === 0 && (
                  <EmptyText>{t('performance.pip.noGoals')}</EmptyText>
                )}
                {(detail.goals || []).map((g) => (
                  <GoalCard key={g.id}>
                    <strong>{g.title}</strong>
                    {g.isAchieved != null && (
                      <AchievedBadge $achieved={g.isAchieved}>
                        {g.isAchieved ? t('performance.pip.goalAchieved') : t('performance.pip.goalNotAchieved')}
                      </AchievedBadge>
                    )}
                    {g.successCriteria && <div style={{ marginTop: 4 }}>{g.successCriteria}</div>}
                    {g.note && <SmallMeta>{g.note}</SmallMeta>}
                  </GoalCard>
                ))}
              </Section>

              <Section>
                <SectionTitle>{t('performance.pip.checkIns')}</SectionTitle>
                {(detail.checkIns || []).length === 0 && (
                  <EmptyText>{t('performance.pip.noCheckIns')}</EmptyText>
                )}
                {(detail.checkIns || []).map((c) => (
                  <CheckInCard key={c.id}>
                    <div>{c.note}</div>
                    <SmallMeta>{formatDate(c.createDate, lang)}</SmallMeta>
                  </CheckInCard>
                ))}
              </Section>
            </>
          )}
        </Card>
      )}
    </Container>
  );
}
